import React, { useState, useEffect } from 'react';
import { letterTemplatesService, letterGeneratorService, generatedLettersService } from '../../services/letterService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './LetterGenerator.css';

const LetterGenerator = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const fetchedTemplates = await letterTemplatesService.getAllTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setGeneratedLetter(null);
    
    // Initialize form data with empty values for all placeholders
    const initialFormData = {};
    template.placeholders?.forEach(placeholder => {
      initialFormData[placeholder] = '';
    });
    setFormData(initialFormData);
  };

  const handleInputChange = (placeholder, value) => {
    setFormData(prev => ({
      ...prev,
      [placeholder]: value
    }));
  };

  const handleGenerateLetter = () => {
    if (!selectedTemplate) return;
    
    setGenerating(true);
    
    // Small delay to show loading state
    setTimeout(() => {
      try {
        const generated = letterGeneratorService.generateLetter(selectedTemplate, formData);
        setGeneratedLetter(generated);
      } catch (error) {
        console.error('Error generating letter:', error);
        alert('Failed to generate letter');
      } finally {
        setGenerating(false);
      }
    }, 500);
  };

  const handleSaveLetter = async () => {
    if (!generatedLetter) return;
    
    try {
      setSaving(true);
      await generatedLettersService.saveGeneratedLetter(generatedLetter);
      alert('Letter saved successfully!');
    } catch (error) {
      console.error('Error saving letter:', error);
      alert('Failed to save letter');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedLetter) return;
    
    try {
      const element = document.getElementById('generated-letter-content');
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        letterRendering: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const filename = `${selectedTemplate.name}_${new Date().toLocaleDateString()}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  const handleReset = () => {
    setSelectedTemplate(null);
    setFormData({});
    setGeneratedLetter(null);
  };

  if (loading) {
    return (
      <div className="letter-generator">
        <div className="loading">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="letter-generator">
      <div className="header">
        <h1>Letter Generator</h1>
        {selectedTemplate && (
          <button className="btn btn-secondary" onClick={handleReset}>
            Select Different Template
          </button>
        )}
      </div>

      {!selectedTemplate ? (
        <div className="template-selection">
          <h2>Select a Template</h2>
          {templates.length === 0 ? (
            <div className="empty-state">
              <p>No templates available. Create templates first to generate letters.</p>
            </div>
          ) : (
            <div className="templates-grid">
              {templates.map(template => (
                <div key={template.id} className="template-card" onClick={() => handleTemplateSelect(template)}>
                  <div className="template-header">
                    <h3>{template.name}</h3>
                    <span className={`category-badge ${template.category}`}>
                      {template.category}
                    </span>
                  </div>
                  {template.description && (
                    <p className="template-description">{template.description}</p>
                  )}
                  <div className="template-placeholders">
                    <strong>Fields to fill:</strong>
                    <div className="placeholders-list">
                      {template.placeholders?.map(placeholder => (
                        <span key={placeholder} className="placeholder-tag">
                          {placeholder}
                        </span>
                      )) || <span className="no-placeholders">No fields</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="letter-generation">
          <div className="generation-container">
            <div className="form-section">
              <h2>Fill Letter Details</h2>
              <div className="selected-template-info">
                <h3>{selectedTemplate.name}</h3>
                {selectedTemplate.description && <p>{selectedTemplate.description}</p>}
              </div>
              
              <div className="form-fields">
                {selectedTemplate.placeholders?.map(placeholder => (
                  <div key={placeholder} className="form-group">
                    <label htmlFor={placeholder}>
                      {placeholder.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                    <input
                      type="text"
                      id={placeholder}
                      value={formData[placeholder] || ''}
                      onChange={(e) => handleInputChange(placeholder, e.target.value)}
                      placeholder={`Enter ${placeholder.replace(/_/g, ' ')}`}
                    />
                  </div>
                ))}
              </div>
              
              <div className="form-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleGenerateLetter}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Letter'}
                </button>
              </div>
            </div>
            
            <div className="preview-section">
              <h2>Letter Preview</h2>
              <div className="preview-container">
                {generatedLetter ? (
                  <div>
                    <div id="generated-letter-content" className="letter-content">
                      <div className="letter-header">
                        <div className="letterhead">
                          <h2>GREY OFFICIAL</h2>
                          <p>Official Document</p>
                        </div>
                        <div className="letter-date">
                          Date: {new Date().toLocaleDateString()}
                        </div>
                      </div>
                      <div className="letter-body">
                        {generatedLetter.content.split('\n').map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                      <div className="letter-footer">
                        <p>Sincerely,</p>
                        <p>Grey Official Management</p>
                      </div>
                    </div>
                    
                    <div className="letter-actions">
                      <button 
                        className="btn btn-primary"
                        onClick={handleDownloadPDF}
                      >
                        Download PDF
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={handleSaveLetter}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Letter'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <p>Fill in the details and click "Generate Letter" to see the preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterGenerator;